




































































































import Vue from 'vue';
import _ from 'lodash';
import { mapActions } from 'vuex';

import ZenMultiselect from '@reciprocity/multiselect';
import { ZenFormGroup } from 'zenComponents';
import { BRow, BCol, BFormCheckbox } from 'bootstrap-vue';

import { transformIssueFields, isFieldTitleCopiable } from './issueFields';
import IssueField from './IssueField.vue';

const EXCLUDE_FIELDS = ['project', 'issuetype', 'summary', 'reporter'];

export default Vue.extend({
  name: 'AuditInfoJira',
  components: {
    ZenMultiselect,
    IssueField,
    ZenFormGroup,
    BRow,
    BCol,
    BFormCheckbox,
  },
  props: {
    title: {
      type: String,
      required: false,
      default: null,
    },
    initialJiraData: {
      type: Object,
      required: true,
    },
    syncExternalComments: {
      type: Boolean,
      required: true,
    },
    syncExternalAttachments: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    const initial = this.initialJiraData;

    return {
      isEditing: initial.projectId != null,
      blockEvents: false,

      project: null,
      loadingJiraProject: false,
      projects: [],
      projectsIsLoaded: false,
      projectsLoadedError: undefined,

      issueLinkTypes: [],
      issueLinkTypesIsLoaded: false,
      issueLinkTypesLoadedError: undefined,

      projectId: initial.projectId,
      issueTypeId: initial.issueTypeId,
      requestIssueTypeId: initial.requestIssueTypeId,
      requestIssueLinkTypeId: initial.requestIssueLinkTypeId,
      auditIssue: _.cloneDeep(initial.auditIssue),
    };
  },
  computed: {
    isAllLoaded() {
      return this.projectsIsLoaded && this.issueLinkTypesIsLoaded;
    },
    projectReady() {
      return this.project && !this.loadingJiraProject;
    },
    loadError() {
      if (this.projectsLoadedError) {
        return this.projectsLoadedError;
      } else if (this.issueLinkTypesLoadedError) {
        return this.issueLinkTypesLoadedError;
      }

      return undefined;
    },
    projectComputedValue() {
      if (!_.isObject(this.projectId)) {
        return _.find(this.projectDropdownValues, ['value', _.toString(this.projectId)]);
      }
      return this.projectId;
    },
    projectDropdownValues() {
      return this.projects.map(project => ({
        value: project.id,
        title: project.name,
      }));
    },
    issueLinkTypeComputedValue() {
      if (!_.isObject(this.requestIssueLinkTypeId)) {
        return _.find(this.issueLinkTypeDropdownValues, ['value', _.toString(this.requestIssueLinkTypeId)]);
      }
      return this.requestIssueLinkTypeId;
    },
    issueLinkTypeDropdownValues() {
      return this.issueLinkTypes.map(linkType => ({
        value: linkType.id,
        title: linkType.name,
      }));
    },
    projectIssueTypes() {
      return this.project != null ? this.project.issueTypes : [];
    },
    issueTypeComputedValue() {
      if (!_.isObject(this.issueTypeId)) {
        return _.find(this.issueTypeDropdownValues, ['value', _.toString(this.issueTypeId)]);
      }
      return this.issueTypeId;
    },
    requestIssueTypeComputedValue() {
      if (!_.isObject(this.requestIssueTypeId)) {
        return _.find(this.issueTypeDropdownValues, ['value', _.toString(this.requestIssueTypeId)]);
      }
      return this.requestIssueTypeId;
    },
    issueTypeDropdownValues() {
      return this.projectIssueTypes.map(issueType => ({
        value: issueType.id,
        title: issueType.name,
      }));
    },
    issueType() {
      return this.projectIssueTypes.find(issueType => issueType.id === this.issueTypeId);
    },
    requestIssueType() {
      return this.projectIssueTypes.find(issueType => issueType.id === this.requestIssueTypeId);
    },
    requestIssueLinkType() {
      return this.issueLinkTypes.find(linkType => linkType.id === this.requestIssueLinkTypeId);
    },
    auditIssueRequiredFields() {
      if (this.issueType == null) {
        return [];
      }
      const fields = transformIssueFields(this.issueType.fields);
      const filteredFields = fields.filter(field => field.required && EXCLUDE_FIELDS.indexOf(field.key) === -1);
      return _.sortBy(filteredFields, field => /^customfield/.test(field.key));
    },
    auditIssueTitleCopiableFields() {
      return this.auditIssueRequiredFields.filter(isFieldTitleCopiable);
    },
    isAuditIssueVisible() {
      return !this.isEditing && this.auditIssue && this.auditIssueRequiredFields.length > 0;
    },
    isAuditIssueValid() {
      if (this.isEditing) {
        return true;
      }
      if (this.auditIssue == null) {
        return false;
      }
      return _.every(this.auditIssueRequiredFields, field => this.auditIssue.fields[field.key]);
    },
    /* eslint-disable complexity */
    isValid() {
      if (!this.isAllLoaded) {
        return false;
      }

      return (
        this.projectId != null &&
        this.issueTypeId != null &&
        this.requestIssueTypeId != null &&
        this.requestIssueLinkTypeId != null &&
        this.isAuditIssueValid
      );
    },
    /* eslint-enable complexity */
    jiraData() {
      const issueTypeId = _.get(this.issueTypeId, 'value', this.issueTypeId);
      const requestIssueTypeId = _.get(this.requestIssueTypeId, 'value', this.requestIssueTypeId);
      const requestIssueLinkTypeId = _.get(this.requestIssueLinkTypeId, 'value', this.requestIssueLinkTypeId);

      // this object is manually transformed to snake case in
      // step_1.js saveAudit(), so keep this in sync
      return {
        projectId: this.projectId,
        issueTypeId: issueTypeId,
        requestIssueTypeId: requestIssueTypeId,
        requestIssueLinkTypeId: requestIssueLinkTypeId,
        auditIssue: _.cloneDeep(this.auditIssue),
        isValid: this.isValid,
      };
    },
    projectFieldDescription() {
      return this.isEditing ? 'Jira project cannot be changed for existing Audit.' : null;
    },
    issueFieldDescription() {
      return this.isEditing ? 'Audit issue type cannot be changed for existing Audit.' : null;
    },
  },
  watch: {
    title(title, oldTitle) {
      this.auditIssueCopyTitle(title, oldTitle);
    },
    jiraData: {
      handler(data) {
        this.setJiraData(data);
      },
      deep: true,
    },
    /*
     * The following properties need watchers in order to handle the firing of their
     * corresponding listeners if the 'vue-multiselect' component is pre-populated
     * (receives a pre-selected option as `:value`).
     */
    issueTypeComputedValue(issueType) {
      this.onIssueTypeInput(issueType);
    },
    requestIssueTypeComputedValue(requestIssueType) {
      this.onRequestIssueTypeInput(requestIssueType);
    },
    issueLinkTypeComputedValue(issueLinkType) {
      this.onIssueLinkTypeInput(issueLinkType);
    },
    //
  },
  async created() {
    try {
      await this.projectsLoad();
      await this.issueLinkTypesLoad();

      if (this.isEditing) {
        // projectId can be either an object with .value prop (a number) or the
        // number itself
        const projectId = _.get(this.projectId, 'value', this.projectId);

        await this.loadProjectData(projectId);

        const loadedData = this.initialJiraData;

        this.issueTypeId = loadedData.issueTypeId;
        this.requestIssueTypeId = loadedData.requestIssueTypeId;
        this.requestIssueLinkTypeId = loadedData.requestIssueLinkTypeId;
      }
    } catch (error) {
      this.showAlert({
        type: 'error',
        message: 'Oops, something went wrong.',
      });
      throw error;
    }
  },
  methods: {
    ...mapActions({
      showAlert: 'alerts/show',
    }),
    ...mapActions('auditSetupWizard', [
      'setJiraData',
      'loadJiraProjects',
      'loadJiraProject',
      'loadJiraIssueLinkTypes',
      'setSyncExternalComments',
      'setSyncExternalAttachments',
    ]),
    async projectsLoad() {
      this.projectsIsLoaded = false;
      try {
        const projects = await this.loadJiraProjects();
        this.projectsLoaded(projects);
        return Promise.resolve(projects);
      } catch (error) {
        let msg = 'Failed to load the list of Jira projects';
        if (_.has(error, 'response.data.message')) {
          msg += ': ' + error.response.data.message;
        }
        this.projectsLoadedError = msg;
        throw error;
      }
    },
    projectsLoaded(projects) {
      this.projects = projects;
      this.projectsIsLoaded = true;
    },
    async issueLinkTypesLoad() {
      this.issueLinkTypesIsLoaded = false;
      try {
        const issueLinkTypes = await this.loadJiraIssueLinkTypes();
        this.issueLinkTypesLoaded(issueLinkTypes);
        return Promise.resolve(issueLinkTypes);
      } catch (error) {
        let msg = 'Failed to load the list of Jira issue link types';
        if (_.has(error, 'response.data.message')) {
          msg += ': ' + error.response.data.message;
        }
        this.issueLinkTypesLoadedError = msg;
        throw error;
      }
    },
    issueLinkTypesLoaded(issueLinkTypes) {
      this.issueLinkTypes = issueLinkTypes;
      this.issueLinkTypesIsLoaded = true;

      if (this.requestIssueLinkTypeId == null) {
        issueLinkTypes.forEach(linkType => {
          if (linkType.isDefault) {
            this.requestIssueLinkTypeId = {
              value: linkType.id,
              title: linkType.name,
            };
          }
        });
      }
    },
    // eslint-disable-next-line complexity
    async loadProjectData(projectId) {
      try {
        this.loadingJiraProject = true;
        const createMeta = await this.loadJiraProject(projectId);
        this.project = createMeta;

        if (!this.isEditing) {
          // If we're creating a new audit select the default Audit issue type
          // for this project if this project's issue types contain the default
          // from Jira settings.
          if (this.project != null) {
            this.issueTypeId =
              _.find(this.issueTypeDropdownValues, {
                value: this.project.auditIssueTypeId,
              }) || undefined;
          } else {
            this.issueTypeId = undefined;
          }
          // Initialize audit issue if any fields for audit's issue type are
          // required.
          this.initializeAuditIssue();

          // If we're creating a new audit select the default Request issue type
          // for this project if this project's issue types contain the default
          // from Jira settings.
          if (this.project != null) {
            this.requestIssueTypeId =
              _.find(this.issueTypeDropdownValues, {
                value: this.project.requestIssueTypeId,
              }) || undefined;
          } else {
            this.requestIssueTypeId = undefined;
          }
        }
      } catch (error) {
        this.showAlert({
          type: 'error',
          message: 'An error occurred. Are you sure the project has issue ' + 'types?',
        });
        throw error;
      } finally {
        this.loadingJiraProject = false;
      }
    },
    onProjectInput(value) {
      this.projectId = value.value;
      this.loadProjectData(value.value);
    },
    onIssueTypeInput(value) {
      this.issueTypeId = value.value;

      this.initializeAuditIssue();
    },
    onRequestIssueTypeInput(value) {
      this.requestIssueTypeId = value.value;
    },
    onIssueLinkTypeInput(value) {
      this.requestIssueLinkTypeId = value.value;
    },
    initializeAuditIssue() {
      const oldIssue = _.cloneDeep(this.auditIssue);

      const fields = {};

      this.auditIssueRequiredFields.forEach(field => {
        const value = oldIssue && oldIssue.fields[field.key] ? oldIssue.fields[field.key] : undefined;

        fields[field.key] = value;
      });

      this.auditIssue = {
        fields: fields,
      };

      this.auditIssueCopyTitle(this.title, this.title);
    },
    auditIssueCopyTitle(title, oldTitle) {
      this.auditIssueTitleCopiableFields.forEach(field => {
        const fieldValue = this.auditIssue.fields[field.key];
        if (!fieldValue || fieldValue === oldTitle) {
          this.auditIssue.fields[field.key] = title;
        }
      });
    },
  },
});
